import type { components } from "@roastery/api-client";
import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useApi } from "./api-context";
import { rememberIdentity, useOfflineIdentity } from "./offline-identity";

/** Identity of the caller as returned by GET /api/v1/me. */
export type CurrentUser = components["schemas"]["Me"];

/** Settings of the roastery the caller belongs to; absent for platform operators. */
export type TenantSummary = components["schemas"]["TenantSummary"];

/**
 * The signed-in user, resolved by the backend rather than by decoding the token in the browser — the
 * backend is the authority on roles and on which tenant the user belongs to.
 *
 * <p>With no session and no network it answers with the last user this device saw (US-33.4), flagged
 * `offline`. That answer draws the screens and hides the buttons; it authorises nothing, because
 * there is no token behind it and every write is queued for a server that will check again.
 */
export function useCurrentUser() {
  const auth = useAuth();
  const api = useApi();
  // Whether the guard above decided this is an offline session, and for whom. One answer, one place.
  const remembered = useOfflineIdentity();

  const query = api.query.useQuery(
    "get",
    "/api/v1/me",
    {},
    {
      enabled: auth.isAuthenticated,
      // The identity only changes on re-login, so refetching on every mount is wasted work.
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  );

  const fresh = query.data as CurrentUser | undefined;

  // Kept for the next cold start, in an effect rather than in render: writing to storage while
  // rendering is the kind of side effect React is allowed to run twice.
  useEffect(() => {
    if (fresh) {
      rememberIdentity(fresh);
    }
  }, [fresh]);

  if (remembered && !auth.isAuthenticated) {
    return {
      user: remembered.user,
      isLoading: false,
      error: undefined,
      offline: true as const,
      savedAt: remembered.savedAt,
    };
  }

  return {
    user: fresh,
    isLoading: auth.isLoading || query.isLoading,
    error: query.error,
    offline: false as const,
    savedAt: undefined,
  };
}

export function hasRole(user: CurrentUser | undefined, ...roles: string[]): boolean {
  if (!user?.roles) {
    return false;
  }
  return roles.some((role) => user.roles?.includes(role));
}
